import React, { useState } from 'react';
import {
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Badge,
  alpha,
  Fab,
} from '@mui/material';
import {
  Home as HomeIcon,
  DirectionsCar as InventoryIcon,
  Add as AddIcon,
  CalendarMonth as CalendarIcon,
  AutoAwesome as AIIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

interface MobileBottomNavProps {
  onOpenQuickAdd: () => void;
}

export default function MobileBottomNav({
  onOpenQuickAdd,
}: MobileBottomNavProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/') return 0;
    if (path.startsWith('/inventory')) return 1;
    if (path === '/ai') return 3;
    if (path === '/calendar') return 4;
    return -1;
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        pb: 'env(safe-area-inset-bottom)', // iOS safe area
      }}
    >
      <BottomNavigation
        value={getActiveTab()}
        onChange={(event, newValue) => {
          if (newValue === 0) navigate('/');
          if (newValue === 1) navigate('/inventory');
          if (newValue === 3) navigate('/ai');
          if (newValue === 4) navigate('/calendar');
        }}
        showLabels={true}
        sx={{
          height: 56,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 60,
            padding: '4px 12px',
            '&:focus': {
              outline: 'none'
            },
            '&:focus-visible': {
              outline: 'none'
            },
            '& .MuiSvgIcon-root': {
              fontSize: '1.5rem',
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.625rem', // Very tiny font (10px)
              marginTop: '2px',
              fontWeight: 500,
              '&.Mui-selected': {
                fontSize: '0.625rem', // Keep same size when selected
              },
            },
          },
        }}
      >
        {/* Home */}
        <BottomNavigationAction
          label="Home"
          icon={<HomeIcon />}
          sx={{
            color: 'text.secondary',
            '&.Mui-selected': {
              color: 'primary.main',
            },
          }}
        />

        {/* Inventory */}
        <BottomNavigationAction
          label="Inventory"
          icon={<InventoryIcon />}
          sx={{
            color: 'text.secondary',
            '&.Mui-selected': {
              color: 'primary.main',
            },
          }}
        />

        {/* Create - Center FAB */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 80,
            pt: 0.5,
          }}
        >
          <Fab
            color="primary"
            size="medium"
            onClick={onOpenQuickAdd}
            sx={{
              position: 'absolute',
              top: -20,
              bgcolor: 'primary.main',
              boxShadow: `0 4px 12px ${alpha('#10b981', 0.4)}`,
              '&:hover': {
                bgcolor: 'primary.dark',
                boxShadow: `0 6px 16px ${alpha('#10b981', 0.5)}`,
              },
              '&:focus': {
                outline: 'none'
              },
              '&:focus-visible': {
                outline: 'none'
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <AddIcon />
          </Fab>
        </Box>

        {/* AI */}
        <BottomNavigationAction
          label="AI"
          icon={<AIIcon />}
          sx={{
            color: 'text.secondary',
            '&.Mui-selected': {
              color: 'primary.main',
            },
          }}
        />

        {/* Calendar */}
        <BottomNavigationAction
          label="Calendar"
          icon={<CalendarIcon />}
          sx={{
            color: 'text.secondary',
            '&.Mui-selected': {
              color: 'primary.main',
            },
          }}
        />
      </BottomNavigation>
    </Box>
  );
}
